/* CENEC-branded PDF export for the Dataset summary table.
 *
 * Derived from batterySummaryPdf.js — same brand chrome, same font embedding,
 * same save-picker-first ordering — but landscape A4, because a 40-year table
 * with a dozen series columns is unreadable portrait.
 *
 * Save location: the File System Access API picker is opened FIRST, while the
 * click still carries user activation. Building the PDF afterwards would
 * otherwise consume the activation window and Chrome would reject the picker.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// CENEC brand tokens — mirrors templates/paged/cenec-paged.css
const NAVY       = [26, 26, 46];
const NAVY_ALT   = [22, 33, 62];
const GREEN      = [78, 204, 163];
const INK        = [45, 52, 54];
const MUTED      = [138, 154, 149];
const OFF_WHITE  = [240, 244, 243];
const RULE       = [224, 230, 228];

const HEAD = 'SpaceGrotesk';
const BODY = 'SourceSans';

// A4 landscape
const PAGE_W  = 297;
const PAGE_H  = 210;
const MARGIN  = 14;
const CONTENT = PAGE_W - MARGIN * 2;

const FONTS = [
  { file: 'SpaceGrotesk-Medium.ttf',   family: HEAD, style: 'normal' },
  { file: 'SpaceGrotesk-Bold.ttf',     family: HEAD, style: 'bold'   },
  { file: 'SourceSans3-Regular.ttf',   family: BODY, style: 'normal' },
  { file: 'SourceSans3-SemiBold.ttf',  family: BODY, style: 'bold'   },
];

// ---------------------------------------------------------------------------
// formatting
// ---------------------------------------------------------------------------

function num(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '';
  const abs = Math.abs(v);
  const dp = abs >= 1000 ? 0 : abs >= 10 ? 1 : 2;
  return v.toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function stamp(iso, withTime = true) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = (n) => String(n).padStart(2, '0');
  const date = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return withTime ? `${date} ${p(d.getHours())}:${p(d.getMinutes())}` : date;
}

// ---------------------------------------------------------------------------
// asset loading
// ---------------------------------------------------------------------------

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunked — String.fromCharCode blows the argument limit on a 230 KB font.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return window.btoa(binary);
}

async function embedFonts(doc) {
  const loaded = await Promise.all(FONTS.map(async (f) => {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/fonts/${f.file}`);
    if (!res.ok) throw new Error(`font ${f.file}: HTTP ${res.status}`);
    return { ...f, b64: toBase64(await res.arrayBuffer()) };
  }));
  loaded.forEach((f) => {
    doc.addFileToVFS(f.file, f.b64);
    doc.addFont(f.file, f.family, f.style);
  });
}

/** Rasterise the brand SVG so jsPDF can place it. Same-origin, so the canvas
 *  stays untainted and toDataURL works. Returns null if anything fails —
 *  the header falls back to a text wordmark. */
async function loadLogo() {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/cenec-logo.svg`);
    if (!res.ok) return null;
    const svg = await res.text();
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = 4;
    const w = 958, h = Math.round(958 / (958.34 / 342.6));
    const canvas = document.createElement('canvas');
    canvas.width = w * scale / 2;
    canvas.height = h * scale / 2;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    return { data: canvas.toDataURL('image/png'), ratio: 958.34 / 342.6 };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// chrome
// ---------------------------------------------------------------------------

function drawHeader(doc, logo, subtitle) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, PAGE_W, 24, 'F');
  doc.setFillColor(...GREEN);
  doc.rect(0, 24, PAGE_W, 1.2, 'F');

  if (logo) {
    const h = 10;
    doc.addImage(logo.data, 'PNG', MARGIN, 7, h * logo.ratio, h);
  } else {
    doc.setFont(HEAD, 'bold');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text('CENEC', MARGIN, 16);
  }

  doc.setFont(BODY, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GREEN);
  doc.text('NETWORK RESOURCE GRID', PAGE_W - MARGIN, 11.5, { align: 'right' });
  doc.setTextColor(220, 226, 224);
  doc.text(subtitle, PAGE_W - MARGIN, 17, { align: 'right' });
}

function drawFooters(doc, reference, generatedAt) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...RULE);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, PAGE_H - 12, PAGE_W - MARGIN, PAGE_H - 12);
    doc.setFont(BODY, 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(reference, MARGIN, PAGE_H - 8);
    doc.text(`Generated ${generatedAt}`, PAGE_W / 2, PAGE_H - 8, { align: 'center' });
    doc.text(`Page ${i} of ${pages}`, PAGE_W - MARGIN, PAGE_H - 8, { align: 'right' });
  }
}

/** Muted explanatory paragraph. Measures its own wrapping so the caller
 *  advances by the real height instead of a guess. */
function note(doc, text, y) {
  doc.setFont(BODY, 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...MUTED);
  const lines = doc.splitTextToSize(text, CONTENT);
  doc.text(lines, MARGIN, y);
  return y + lines.length * 3.1;
}

// ---------------------------------------------------------------------------
// body
// ---------------------------------------------------------------------------

function buildDocument(doc, ctx, logo, generatedAt) {
  const { projectCode, projectName, dataset, summary, query } = ctx;
  const subtitle = `${projectCode || 'Project'} · Dataset summary`;

  drawHeader(doc, logo, subtitle);

  let y = 36;
  doc.setFont(HEAD, 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...NAVY);
  doc.text('Dataset Summary', MARGIN, y);

  y += 6;
  doc.setFont(BODY, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text(`${projectCode ? `${projectCode} — ` : ''}${projectName || ''}`, MARGIN, y);

  // Source line: everything needed to reproduce the table.
  y += 6;
  const source = [
    `Dataset: ${dataset.name || 'unnamed'}`,
    `tool: ${dataset.tool || '—'}`,
    `exported: ${stamp(dataset.exported_at, false)}`,
    `span: ${stamp(dataset.ts_start, false)} → ${stamp(dataset.ts_end, false)}`,
    `series in dataset: ${dataset.series_count ?? '—'}`,
  ].join(' · ');
  y = note(doc, source, y);

  const q = [
    `Query: period ${query.period}`,
    `range ${query.yearFrom}–${query.yearTo}`,
    `stock statistic ${query.stat}`,
    `mass ${query.massUnit}`,
    `energy ${query.energyUnit}`,
    `columns ${summary.columns.length}`,
    `rows ${summary.rows.length}`,
    `exported ${generatedAt}`,
  ].join(' · ');
  y = note(doc, q, y + 1);

  y = note(
    doc,
    'Stock series are state-of-charge readings, never sums: their column carries the selected statistic and no total.',
    y + 1,
  );

  const head = [['Period', ...summary.columns.map((c) => `${c.display_name}${c.unit ? `\n[${c.unit}]` : ''}${c.is_stock ? '\n(stock)' : ''}`)]];
  const body = summary.rows.map((r) => [r.period, ...r.values.map(num)]);
  const foot = [['Total', ...summary.columns.map((c, i) => (
    c.is_stock || summary.totals[i] === null || summary.totals[i] === undefined ? '' : num(summary.totals[i])
  ))]];

  const columnStyles = {};
  summary.columns.forEach((c, i) => { columnStyles[i + 1] = { halign: 'right' }; });

  autoTable(doc, {
    startY: y + 4,
    head,
    body,
    foot,
    theme: 'plain',
    margin: { left: MARGIN, right: MARGIN, top: 32, bottom: 18 },
    // Any table may spill; a continuation page must still carry the brand band.
    didDrawPage: (data) => { if (data.pageNumber > 1) drawHeader(doc, logo, subtitle); },
    styles: {
      font: BODY, fontSize: 7.5, cellPadding: { top: 1.6, bottom: 1.6, left: 2, right: 2 },
      textColor: INK, lineColor: RULE, lineWidth: 0, overflow: 'linebreak',
    },
    headStyles: {
      font: HEAD, fontStyle: 'bold', fontSize: 7, textColor: [255, 255, 255],
      fillColor: NAVY_ALT, lineWidth: 0, halign: 'right',
    },
    footStyles: {
      font: HEAD, fontStyle: 'bold', fontSize: 7.5, textColor: NAVY,
      fillColor: [226, 236, 232], lineWidth: 0, halign: 'right',
    },
    alternateRowStyles: { fillColor: OFF_WHITE },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' }, ...columnStyles },
  });

  drawFooters(doc, `${projectCode || 'NRG'} · ${dataset.name || 'dataset'}`, generatedAt);
}

// ---------------------------------------------------------------------------
// entry point
// ---------------------------------------------------------------------------

/**
 * Build and save the dataset summary PDF.
 * ctx = { projectCode, projectName, dataset, summary, query }
 * Returns { saved: true, filename } | { cancelled: true } and throws on failure.
 */
export async function exportDatasetSummaryPdf(ctx) {
  if (!ctx || !ctx.summary || !Array.isArray(ctx.summary.columns) || !Array.isArray(ctx.summary.rows)) {
    throw new Error('summary payload incomplete — reload the table before exporting');
  }
  if (!ctx.dataset) throw new Error('dataset missing from export context');

  const now = new Date();
  const p = (n) => String(n).padStart(2, '0');
  const dateSlug = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  const generatedAt = `${dateSlug} ${p(now.getHours())}:${p(now.getMinutes())}`;
  const code = (ctx.projectCode || 'NRG').replace(/[^A-Za-z0-9]+/g, '');
  const { period, yearFrom, yearTo } = ctx.query;
  const filename = `${code}_dataset-summary_${period}_${yearFrom}-${yearTo}.pdf`;

  // Ask where to save first — the picker needs the click's user activation,
  // and embedding ~600 KB of fonts would spend it.
  let handle = null;
  if (window.showSaveFilePicker) {
    try {
      handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
      });
    } catch (err) {
      if (err && err.name === 'AbortError') return { cancelled: true };
      handle = null;   // unsupported or blocked — fall back to a download
    }
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape', compress: true });
  await embedFonts(doc);
  const logo = await loadLogo();
  buildDocument(doc, ctx, logo, generatedAt);

  const blob = doc.output('blob');

  if (handle) {
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return { saved: true, filename: handle.name || filename };
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { saved: true, filename };
}

export default exportDatasetSummaryPdf;
